import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams
} from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '@env/environment';
import {
  ExtractionPreviewRepository,
  ApprovalResult,
  BoxSummary,
  StorageInventoryExportRequest
} from '@service/extraction-preview/extraction-preview.repository';
import { ExtractionPreview } from '@model/extraction-preview/extraction-preview.model';
import { PaginatedResponse } from '@model/common/pagination.model';
import {
  ExtractionPreviewQueryParams,
  DEFAULT_EXTRACTION_PREVIEW_QUERY
} from '@model/extraction-preview/extraction-preview-query-params.model';
import { NotFoundException } from '@model/common/domain.exception';
import {
  SequencingReprocess,
  ReportSequencingReprocessRequest
} from '@model/catalog/catalog.model';

/**
 * Backend API response for extraction preview
 * Raw format before transformation
 */
interface ExtractionPreviewApiResponse {
  extractionId: string;
  specimenId: string;
  specimenCode: string;
  lgCode: string;
  patientId: string;
  patientIdNumber: string;
  patientGivenName: string;
  patientMiddleName: string | null;
  patientFamilyName: string;
  patientMaternalFamilyName: string | null;
  birthDate: string; // ISO format: yyyy-MM-ddTHH:mm:ss
  administrativeSex: string;
  examCode: string;
  examType: string;
  concentration: number;
  ratio260_280: number;
  ratio260_230: number;
  wellLocation: string;
  extractionDate: string; // ISO format: yyyy-MM-dd
  responsibleName: string;
  extractionMethodId: string;
  extractionMethodName: string;
  status: string;
  priority: string;
  observations: string | null;
  reprocess: boolean | null;
  physicalLocation: string | null;
}

/**
 * HTTP Repository Implementation for Extraction Preview
 * HU28-922486: Listado de muestras de extracción
 *
 * Connects to backend endpoints:
 * - GET /laboratorio/extractions/preview (list extractions)
 * - POST /laboratorio/extractions/preview/approve (approve batch)
 */
@Injectable({
  providedIn: 'root'
})
export class HttpExtractionPreviewRepository extends ExtractionPreviewRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}/extractions/preview`;

  override findAll(
    params?: ExtractionPreviewQueryParams
  ): Observable<PaginatedResponse<ExtractionPreview>> {
    const httpParams = new HttpParams()
      .set(
        'page',
        String(params?.page ?? DEFAULT_EXTRACTION_PREVIEW_QUERY.page)
      )
      .set(
        'size',
        String(params?.size ?? DEFAULT_EXTRACTION_PREVIEW_QUERY.size)
      )
      .set(
        'sortBy',
        params?.sortBy ??
          DEFAULT_EXTRACTION_PREVIEW_QUERY.sortBy ??
          'extractionDate'
      )
      .set(
        'sortDirection',
        params?.sortDirection ??
          DEFAULT_EXTRACTION_PREVIEW_QUERY.sortDirection ??
          'ASC'
      );

    return this.http
      .get<PaginatedResponse<ExtractionPreviewApiResponse>>(this.BASE_URL, {
        params: httpParams
      })
      .pipe(
        map((response) => this.transformResponse(response)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return throwError(
              () =>
                new NotFoundException('EXTRACTION_PREVIEW_NOT_FOUND', {
                  filters: params
                })
            );
          }
          return throwError(() => error);
        })
      );
  }

  override approve(extractionIds: string[]): Observable<ApprovalResult> {
    return this.http
      .post<ApprovalResult>(`${this.BASE_URL}/approve`, { extractionIds })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Export all extractions to CSV
   * Streams CSV from backend endpoint
   */
  override exportToCSV(
    sortBy: string,
    sortDirection: string
  ): Observable<Blob> {
    const httpParams = new HttpParams()
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    return this.http
      .get(`${this.BASE_URL}/export`, {
        params: httpParams,
        responseType: 'blob'
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  override downloadStorageInventory(): Observable<Blob> {
    return this.http
      .get(`${this.BASE_URL}/storage-inventory`, {
        responseType: 'blob'
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  override listBoxesByDateRange(
    startDate: string,
    endDate: string
  ): Observable<BoxSummary[]> {
    return this.http
      .get<BoxSummary[]>(`${this.BASE_URL}/storage-inventory/boxes`, {
        params: { startDate, endDate }
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  override downloadStorageInventoryWithSelection(
    request: StorageInventoryExportRequest
  ): Observable<Blob> {
    return this.http
      .post(`${this.BASE_URL}/storage-inventory/download`, request, {
        responseType: 'blob'
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  private transformResponse(
    response: PaginatedResponse<ExtractionPreviewApiResponse>
  ): PaginatedResponse<ExtractionPreview> {
    return {
      ...response,
      content: response.content.map((item) => this.mapToDomain(item))
    };
  }

  private mapToDomain(item: ExtractionPreviewApiResponse): ExtractionPreview {
    return {
      extractionId: item.extractionId,
      specimenId: item.specimenId,
      specimenCode: item.specimenCode,
      lgCode: item.lgCode,
      patientIdNumber: item.patientIdNumber,
      patientNames: this.concatenateNames(
        item.patientGivenName,
        item.patientMiddleName
      ),
      patientSurnames: this.concatenateNames(
        item.patientFamilyName,
        item.patientMaternalFamilyName
      ),
      birthDate: this.formatDate(item.birthDate),
      administrativeSex: item.administrativeSex,
      examCode: item.examCode,
      examType: item.examType,
      concentration: item.concentration,
      ratio260_280: item.ratio260_280,
      ratio260_230: item.ratio260_230,
      wellLocation: item.wellLocation,
      extractionDate: this.formatDateOnly(item.extractionDate),
      responsibleName: item.responsibleName,
      extractionMethodName: item.extractionMethodName,
      status: item.status,
      priority: item.priority,
      observations: item.observations,
      reprocess: item.reprocess,
      physicalLocation: item.physicalLocation
    };
  }

  /**
   * Concatenate two name parts, filtering nulls
   */
  private concatenateNames(
    first: string | null,
    second: string | null
  ): string {
    return [first, second].filter(Boolean).join(' ');
  }

  /**
   * Format ISO datetime to DD/MM/YYYY
   * Parses date components directly from ISO string to avoid timezone issues
   */
  private formatDate(isoDate: string | null): string {
    if (!isoDate) {
      return '';
    }
    try {
      // Parse ISO date string directly to avoid timezone conversion
      // Expected format: yyyy-MM-ddTHH:mm:ss or yyyy-MM-dd
      const [datePart] = isoDate.split('T');
      const parts = datePart.split('-');
      if (parts.length !== 3) {
        console.warn('formatDate: Unexpected date format:', isoDate);
        return isoDate;
      }
      const [year, month, day] = parts;
      // Ensure day and month are padded
      const paddedDay = day?.padStart(2, '0') ?? '00';
      const paddedMonth = month?.padStart(2, '0') ?? '00';
      return `${paddedDay}/${paddedMonth}/${year}`;
    } catch {
      console.warn('formatDate: Failed to parse:', isoDate);
      return isoDate;
    }
  }

  /**
   * Format ISO date to DD/MM/YYYY HH:mm
   * Parses date components directly from ISO string to avoid timezone issues
   */
  private formatDateOnly(isoDate: string | null): string {
    if (!isoDate) {
      return '';
    }
    try {
      // Parse ISO date string directly to avoid timezone conversion
      // Expected format: yyyy-MM-dd or yyyy-MM-ddTHH:mm:ss
      const [datePart, timePart] = isoDate.split('T');
      const [year, month, day] = datePart.split('-');
      if (timePart) {
        const [hour, minute] = timePart.split(':');
        return `${day}/${month}/${year} ${hour}:${minute}`;
      }
      return `${day}/${month}/${year}`;
    } catch {
      return isoDate;
    }
  }

  private readonly SPECIMENS_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}/specimens`;

  /**
   * Report a specimen for sequencing reprocess
   * Creates an immutable audit trail record
   */
  override reportSequencingReprocess(
    specimenId: string,
    request: ReportSequencingReprocessRequest
  ): Observable<SequencingReprocess> {
    return this.http
      .post<SequencingReprocessApiResponse>(
        `${this.SPECIMENS_URL}/${specimenId}/reprocess`,
        {
          cause_id: request.causeId,
          status_id: request.statusId,
          observations: request.observations
        }
      )
      .pipe(
        map((response) => this.mapReprocessToDomain(response)),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  private mapReprocessToDomain(
    item: SequencingReprocessApiResponse
  ): SequencingReprocess {
    return {
      sequencingFailureId: item.sequencing_failure_id,
      specimenId: item.specimen_id,
      causesId: item.cause_id,
      statusId: item.status_id,
      observations: item.observations,
      reportedByUserId: item.reported_by_user_id,
      reportedAt: item.reported_at
    };
  }
}

/**
 * Backend API response for sequencing reprocess (snake_case)
 */
interface SequencingReprocessApiResponse {
  sequencing_failure_id: string;
  specimen_id: string;
  cause_id: string;
  status_id: string;
  observations: string | null;
  reported_by_user_id: string | null;
  reported_at: string;
}
